import { MultipleQueriesQuery, SearchResponse } from '@algolia/client-search';
import { AnyAction, createAsyncThunk } from '@reduxjs/toolkit';
import { SearchClient } from 'algoliasearch';
import { ThunkDispatch } from 'redux-thunk';

import Sentry from 'config/sentry';

import {
  Market,
  ParticipantType,
  StoryTileFragment,
} from 'graphql/generated/graphql';

import { localizedSearchableField } from 'hooks/search/helpers';
import { SearchSortBy } from 'hooks/search/types';
import { AlgoliaIndex } from 'hooks/useAlgolia';

import {
  AlgoliaFiltersState,
  AlgoliaStory,
  AvailableFilters,
  DurationRange,
  IAlgoliaTags,
  Screen,
  SearchParams,
  SelectedFilters,
  StoryIntro,
  setAvailableFilters,
  setErrored,
  setLoaded,
  setLoading,
  setResults,
  setResultsFilterScreen,
} from 'store/modules/algolia_filters';
import { AuthState } from 'store/modules/auth';
import { ProfileState } from 'store/modules/profile';
import { TranslationsState } from 'store/modules/translations';

import { ContentType } from 'types/contentType';
import { LanguageIntensity } from 'types/languageIntensity';

import { UserAlreadyListenedStoriesState } from '../userAlreadyListenedStories';

type RelevantState = {
  auth: AuthState;
  algolia_filters: AlgoliaFiltersState;
  profile: ProfileState;
  translations: TranslationsState;
  userAlreadyListenedStories: UserAlreadyListenedStoriesState;
};

type BuildFacetFilterParams = {
  baseFilter: string;
  facet: string;
  facetFilters: (string | string[])[];
  indexName: string;
  indexToExclude: number;
};

type BuildAllFacetFilters = {
  baseFilter: string;
  locale: string;
  selectedFilters: SelectedFilters;
  storiesIndexName: string;
};

type DoAlgoliaSearchParams = {
  query: string;
  baseFilter: string;
  client: SearchClient;
  locale: string;
  nextPage: number;
  indexName: (index: AlgoliaIndex, sortBy: SearchSortBy) => string;
  selectedFilters: SelectedFilters;
  trackingId: string | undefined;
};

type DoAlgoliaSearchResults = {
  facetsUsed: string[];
  results: SearchResponse<AlgoliaStory>[];
};

type BuildBaseFilterParams = {
  searchParams: SearchParams;
  storyMarkets?: string[];
  locale: string;
  excludeAlreadyListenedStories: boolean | undefined;
  alreadyListenedStories: string[];
};

type ConvertHitsParams = {
  hits: Array<AlgoliaStory>;
  locale: string;
};

type ExtractFiltersFromResultsParams = {
  facetsUsed: string[];
  results: SearchResponse<AlgoliaStory>[];
};

type DispatchResultsParams = {
  dispatch: ThunkDispatch<RelevantState, unknown, AnyAction>;
  getState: () => RelevantState;
  locale: string;
  nextPage: number;
  results: SearchResponse<AlgoliaStory>[];
  screen: Screen;
};

const STORY_MARKET_TO_LOCALE: Record<Market, string> = {
  [Market.EnUs]: 'en-US',
  [Market.EnGb]: 'en-GB',
  [Market.FrFr]: 'fr-FR',
  [Market.De]: 'de',
};

const HITS_PER_PAGE = 100;

const FACETS = [
  'content_type.slug',
  'duration_in_range_v2',
  'language_intensity.slug',
  'speaker.participants_type.slug',
  'theme_tag.id',
  'scenario_tag.id',
  'intro',
  'tags.id',
];

const buildFacetFilter: (
  params: BuildFacetFilterParams,
) => MultipleQueriesQuery = ({
  baseFilter,
  facet,
  facetFilters,
  indexName,
  indexToExclude,
}) => ({
  indexName,
  params: {
    facetFilters: facetFilters.filter(
      (_facetFilter, index) => index !== indexToExclude,
    ),
    facets: [facet],
    filters: baseFilter,
    hitsPerPage: 0,
  },
});

const buildAllFacetFilters = ({
  baseFilter,
  locale,
  selectedFilters: {
    contentTypes,
    durationRanges,
    languageIntensities,
    noGoes,
    scenarios,
    participantsTypes,
    themeTags,
    tagIds,
    intros,
  },
  storiesIndexName,
}: BuildAllFacetFilters) => {
  const facetFilters = [
    contentTypes.map((contentType) => `content_type.slug:${contentType}`),
    durationRanges.map((range) => `duration_in_range_v2:${range}`),
    languageIntensities.map(
      (intensity) => `language_intensity.slug:${intensity}`,
    ),
    participantsTypes.map((type) => `speaker.participants_type.slug:${type}`),
    themeTags.map((tag) => `theme_tag.id:${tag}`),
    scenarios.map((scenario) => `scenario_tag.id:${scenario}`),
    intros.map((intro) => `intro:${intro}`),
    tagIds.map((tag) => `tags.id:${tag}`),
    ...noGoes.map(
      (noGo) => `tags.${localizedSearchableField('name', locale)}:-${noGo}`,
    ),
  ];

  const facetsUsed: string[] = [];
  const facetQueries: MultipleQueriesQuery[] = [];

  if (contentTypes.length > 0) {
    facetsUsed.push('content_type.slug');
    facetQueries.push(
      buildFacetFilter({
        baseFilter,
        facet: 'content_type.slug',
        facetFilters,
        indexName: storiesIndexName,
        indexToExclude: 0,
      }),
    );
  }

  if (durationRanges.length > 0) {
    facetsUsed.push('duration_in_range_v2');
    facetQueries.push(
      buildFacetFilter({
        baseFilter,
        facet: 'duration_in_range_v2',
        facetFilters,
        indexName: storiesIndexName,
        indexToExclude: 1,
      }),
    );
  }

  if (languageIntensities.length > 0) {
    facetsUsed.push('language_intensity.slug');
    facetQueries.push(
      buildFacetFilter({
        baseFilter,
        facet: 'language_intensity.slug',
        facetFilters,
        indexName: storiesIndexName,
        indexToExclude: 2,
      }),
    );
  }

  if (participantsTypes.length > 0) {
    facetsUsed.push('speaker.participants_type.slug');
    facetQueries.push(
      buildFacetFilter({
        baseFilter,
        facet: 'speaker.participants_type.slug',
        facetFilters,
        indexName: storiesIndexName,
        indexToExclude: 3,
      }),
    );
  }

  if (themeTags.length > 0) {
    facetsUsed.push('theme_tag.id');
    facetQueries.push(
      buildFacetFilter({
        baseFilter,
        facet: 'theme_tag.id',
        facetFilters,
        indexName: storiesIndexName,
        indexToExclude: 4,
      }),
    );
  }

  if (scenarios.length > 0) {
    facetsUsed.push('scenario_tag.id');
    facetQueries.push(
      buildFacetFilter({
        baseFilter,
        facet: 'scenario_tag.id',
        facetFilters,
        indexName: storiesIndexName,
        indexToExclude: 5,
      }),
    );
  }

  if (intros.length > 0) {
    facetsUsed.push('intro');
    facetQueries.push(
      buildFacetFilter({
        baseFilter,
        facet: 'intro',
        facetFilters,
        indexName: storiesIndexName,
        indexToExclude: 6,
      }),
    );
  }

  if (tagIds.length > 0) {
    facetsUsed.push('tags.id');
    facetQueries.push(
      buildFacetFilter({
        baseFilter,
        facet: 'tags.id',
        facetFilters,
        indexName: storiesIndexName,
        indexToExclude: 7,
      }),
    );
  }

  return { facetFilters, facetQueries, facetsUsed };
};

const doAlgoliaSearch = async ({
  query,
  baseFilter,
  client,
  indexName,
  locale,
  nextPage,
  selectedFilters: { sorting },
  selectedFilters,
  trackingId,
}: DoAlgoliaSearchParams): Promise<DoAlgoliaSearchResults> => {
  try {
    const storiesIndexName = indexName('Stories', sorting);

    const { facetFilters, facetQueries, facetsUsed } = buildAllFacetFilters({
      baseFilter,
      locale,
      selectedFilters,
      storiesIndexName,
    });

    const { results } = await client.multipleQueries<AlgoliaStory>([
      {
        indexName: storiesIndexName,
        query,
        params: {
          enablePersonalization: !!trackingId,
          facetFilters,
          facets: FACETS,
          filters: baseFilter,
          hitsPerPage: HITS_PER_PAGE,
          page: nextPage,
          userToken: trackingId,
        },
      },
      ...facetQueries,
    ]);

    return {
      facetsUsed,
      results,
    };
  } catch (error) {
    Sentry.captureException(new Error(error.message));

    return {
      facetsUsed: [],
      results: [],
    };
  }
};

export const buildBaseFilter = ({
  searchParams,
  storyMarkets,
  locale,
  excludeAlreadyListenedStories,
  alreadyListenedStories,
}: BuildBaseFilterParams) => {
  const filters: Array<string> = [];

  if (searchParams.tagId) {
    filters.push(`tags.id:${searchParams.tagId}`);
  } else if (searchParams.speakerId) {
    filters.push(`speaker.id:${searchParams.speakerId}`);
  } else if (searchParams.genreName) {
    filters.push(
      `genre.${localizedSearchableField('name', locale || 'de')}:'${searchParams.genreName
      }'`,
    );
  } else if (searchParams.contentTypeName) {
    filters.push(`content_type.slug:'${searchParams.contentTypeName}'`);
  } else if (searchParams.languageIntensityName) {
    filters.push(
      `language_intensity.slug:'${searchParams.languageIntensityName}'`,
    );
  }

  if (searchParams.participantsTypes) {
    filters.push(
      searchParams.participantsTypes
        .map(
          (participantType) =>
            `speaker.participants_type.slug:${participantType}`,
        )
        .join(' OR '),
    );
  }

  if (excludeAlreadyListenedStories && alreadyListenedStories.length > 0) {
    filters.push(
      alreadyListenedStories.map((id) => `NOT objectID:${id}`).join(' AND '),
    );
  }

  if (storyMarkets && storyMarkets.length > 0) {
    filters.push(
      `(${storyMarkets
        .map((storyMarket) => `markets:${STORY_MARKET_TO_LOCALE[storyMarket]}`)
        .join(' OR ')})`,
    );
  } else {
    filters.push(`markets:${locale}`);
  }

  return filters.join(' AND ');
};

const convertHits: (
  params: ConvertHitsParams,
) => Array<StoryTileFragment & { tags: Array<IAlgoliaTags> }> = ({
  hits,
  locale,
}) =>
    hits.map((hit) => {
      return {
        alreadyListened: false,
        contentType: {
          slug: hit.content_type.slug,
        },
        durationInMinutes: hit.duration_in_minutes,
        femtasyRed: hit.femtasy_red,
        id: hit.objectID,
        languageIntensity: {
          id: hit.language_intensity.id.toString(),
          slug: hit.language_intensity.slug,
        },
        loved: false,
        playlists: [],
        releaseDate: new Date(hit.release_date_timestamp_seconds).toISOString(),
        speaker: {
          id: hit.speaker.id.toString(),
          name: hit.speaker.name,
          participantsType: {
            slug: hit.speaker.participants_type.slug,
          },
        },
        themeTag: {
          id: hit.theme_tag.id.toString(),
          name: hit.theme_tag[localizedSearchableField('name', locale)],
        },
        title: hit.title,
        description: hit.description,
        tags: hit.tags.map((tag) => ({
          id: tag.id.toString(),
          name: tag[localizedSearchableField('name', locale)],
        })),
      };
    });

export const extractFiltersFromResults: (
  params: ExtractFiltersFromResultsParams,
) => Partial<AvailableFilters> = ({ facetsUsed, results }) =>
    facetsUsed.reduce<Partial<AvailableFilters>>((acc, facet, index) => {
      const facetResult = results[index + 1].facets;
      const value = facetResult?.[facet] ? Object.keys(facetResult[facet]) : [];

      switch (facet) {
        case 'content_type.slug':
          return { ...acc, contentTypes: value as ContentType[] };

        case 'duration_in_range_v2':
          return { ...acc, durationRanges: value as DurationRange[] };

        case 'language_intensity.slug':
          return { ...acc, languageIntensities: value as LanguageIntensity[] };

        case 'speaker.participants_type.slug':
          return { ...acc, participantsTypes: value as ParticipantType[] };

        case 'theme_tag.id':
          return { ...acc, themeTags: value };

        case 'scenario_tag.id':
          return { ...acc, scenarios: value };

        case 'intro':
          return { ...acc, intros: value as StoryIntro[] };

        case 'tags.id':
          return { ...acc, tagIds: value };
      }

      return acc;
    }, {});

const dispatchResults = ({
  dispatch,
  getState,
  locale,
  nextPage,
  results,
  screen,
}: DispatchResultsParams) => {
  const resultsObject = {
    searchResults: results[0],
    stories: convertHits({ locale, hits: results[0].hits }),
    totalCount: results[0].nbHits,
  };

  if (screen === Screen.SearchResultsScreen) {
    if (nextPage === 0) {
      dispatch(setResults(resultsObject));
    } else {
      const {
        algolia_filters: {
          searchScreen: {
            results: { stories: existingStories },
          },
        },
      } = getState();

      dispatch(
        setResults({
          ...resultsObject,
          stories: [...existingStories, ...resultsObject.stories],
        }),
      );
    }
  } else {
    dispatch(setResultsFilterScreen(resultsObject));
  }
  dispatch(setLoaded(true));
};

export const applyFilters = createAsyncThunk<
  void,
  {
    client: SearchClient;
    indexName: (index: AlgoliaIndex, sortBy: SearchSortBy) => string;
    params?: SearchParams;
    screen: Screen;
  },
  {
    state: RelevantState;
  }
>(
  'algoliaFilters/applyFilters',
  async ({ client, indexName, params, screen }, { dispatch, getState }) => {
    try {
      if (getState().algolia_filters.loading) {
        return;
      }

      if (
        screen === Screen.FilterScreen &&
        !getState().algolia_filters.filterScreenChanged
      ) {
        return;
      }

      dispatch(setLoading(true));

      const {
        algolia_filters: {
          searchScreen: {
            results: {
              searchResults: { page: currentSearchScreenPage, nbPages },
            },
          },
          searchParams = params,
        },
      } = getState();

      const nextPage =
        screen === Screen.FilterScreen ? 0 : currentSearchScreenPage + 1;

      if (nextPage > 0 && nextPage >= nbPages) {
        return;
      }

      if (searchParams) {
        const {
          algolia_filters: {
            filterScreen: { selectedFilters, excludeAlreadyListenedStories },
          },
          profile: { user },
          translations: { locale },
          userAlreadyListenedStories: { alreadyListenedStories },
        } = getState();

        const { trackingId, storyMarkets } = user || {
          trackingId: undefined,
          storyMarkets: undefined,
        };

        const baseFilter = buildBaseFilter({
          searchParams,
          storyMarkets,
          locale,
          excludeAlreadyListenedStories,
          alreadyListenedStories,
        });

        const { facetsUsed, results } = await doAlgoliaSearch({
          query: searchParams.title || '',
          baseFilter,
          client,
          indexName: indexName,
          locale,
          nextPage,
          selectedFilters,
          trackingId,
        });

        if (results[0]) {
          dispatchResults({
            dispatch,
            getState,
            locale,
            nextPage,
            results,
            screen,
          });

          const filtersFromResults = extractFiltersFromResults({
            facetsUsed,
            results,
          });

          const {
            algolia_filters: {
              filterScreen: { availableFilters },
            },
          } = getState();

          dispatch(
            setAvailableFilters({
              ...availableFilters,
              ...filtersFromResults,
            }),
          );
        } else {
          dispatch(setErrored(true));
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      dispatch(setErrored(true));
    }
  },
);
